import { useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { createSession } from '@/lib/api';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';

export function AuthCallback() {
  const navigate = useNavigate();
  const location = useLocation();
  const hasProcessed = useRef(false);

  useEffect(() => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const hash = location.hash;
    const sessionId = new URLSearchParams(hash.substring(1)).get('session_id');

    if (!sessionId) {
      console.error('No session_id in callback URL');
      navigate('/login');
      return;
    }

    console.log('Processing OAuth callback with session_id:', sessionId);

    // Exchange session_id for session_token
    createSession(sessionId)
      .then((res) => {
        console.log('Session created successfully:', res.data.user?.email);
        
        // Store session token in localStorage as backup
        if (res.data.session_token) {
          localStorage.setItem('session_token', res.data.session_token);
        }
        
        // Store user data
        localStorage.setItem('user', JSON.stringify(res.data.user));
        
        // Check if this was an admin login attempt
        const isAdminAttempt = sessionStorage.getItem('adminLoginAttempt');
        sessionStorage.removeItem('adminLoginAttempt');
        
        // Get the original destination
        let redirectTo = sessionStorage.getItem('authRedirect') || '/';
        sessionStorage.removeItem('authRedirect');
        
        // If admin login attempt, verify they have admin access
        if (isAdminAttempt === 'true') {
          if (res.data.user?.role === 'admin') {
            redirectTo = '/admin';
            toast.success('Welcome, Admin!');
          } else {
            toast.error('You do not have admin access');
            redirectTo = '/';
          }
        }
        
        // Small delay to ensure cookie is set before navigation
        setTimeout(() => {
          window.location.href = redirectTo;
        }, 100);
      })
      .catch((error) => {
        console.error('Auth failed:', error);
        console.error('Error response:', error.response?.data);
        console.error('Error status:', error.response?.status);
        
        // Check if it's a network error (CORS, domain issues)
        if (!error.response) {
          toast.error('Network error. Please check your connection or try email login.');
        } else {
          const errorDetail = error.response?.data?.detail || 'Authentication failed. Please try again.';
          toast.error(typeof errorDetail === 'string' ? errorDetail : 'Authentication failed. Please try email login instead.');
        }
        navigate('/login', { replace: true });
      });
  }, [location, navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center" data-testid="auth-callback">
      <div className="text-center">
        <Loader2 className="h-12 w-12 animate-spin mx-auto mb-4 text-stone-900" />
        <p className="text-stone-600">Authenticating...</p>
      </div>
    </div>
  );
}